import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import classNames from 'classnames'

import UserService from 'api/Users'
import { isJP } from 'conf'
import { QUOTA_MODE } from 'types/utils'
import { getQuotaStatus, QUOTA_LEVEL } from './quotaStatus'
import styles from './style.module.scss'

const getTooltipKey = (level, max) => {
  if (level !== QUOTA_LEVEL.FULL) return 'record.quota.tooltipUsage'
  // full 이라도 max=0 은 "미할당"으로 안내 문구가 다르다 (인터뷰 확정 — 두 상태가 같은 level 로 수렴)
  return max > 0 ? 'record.quota.tooltipFull' : 'record.quota.tooltipNotAllocated'
}

const ResourceQuotaCard = () => {
  const { t } = useTranslation()
  const paidLicense = useSelector((state) => state.user.paidLicense)
  // menu/RELOAD → GET_DATA 가 매번 새 배열 참조로 SET_STATE — 레코드 생성/삭제 후 재조회 트리거
  const menuData = useSelector((state) => state.menu.menuData)
  const [userQuota, setUserQuota] = useState(null)
  // 연속 RELOAD 시 응답 역전 방지 — 최신 요청의 응답만 반영
  const requestIdRef = useRef(0)

  const enabled = isJP() && paidLicense?.quota_mode === QUOTA_MODE.RESOURCE

  useEffect(() => {
    if (!enabled) return
    requestIdRef.current += 1
    const requestId = requestIdRef.current
    UserService.getMyResourceQuota()
      .then((res) => {
        if (requestIdRef.current !== requestId) return
        setUserQuota(res.data?.user_quota ?? null)
      })
      .catch((error) => {
        if (requestIdRef.current !== requestId) return
        // 조용한 실패 — 보조 정보라 사용자 알림 없이 카드만 숨긴다 (설계 확정)
        console.error('Error fetching resource quota:', error)
        setUserQuota(null)
      })
    // paidLicense 참조 포함: SELECT_WORKSPACE 는 menu/RELOAD 없이 라이선스만 재로드하므로
    // (redux/user/sagas.js SELECT_WORKSPACE), 워크스페이스 전환 시 stale quota 방지
  }, [enabled, paidLicense, menuData])

  if (!enabled || userQuota == null) return null

  // getQuotaStatus 와 동일한 coercion — 비정상 응답이어도 "undefined / undefined" 노출 방지
  const current = Number(userQuota.current_resources) || 0
  const max = Number(userQuota.max_resources) || 0
  const { percent, level } = getQuotaStatus(current, max)

  return (
    <Tooltip title={t(getTooltipKey(level, max), { current, max })} placement="right">
      <div className={classNames(styles.quotaCard, styles[level])}>
        <div className={styles.quotaHeader}>
          <span className={styles.quotaLabel}>{t('record.quota.myResources')}</span>
          <span className={styles.quotaCount}>{`${current} / ${max}`}</span>
        </div>
        <div className={styles.quotaTrack}>
          <div className={styles.quotaFill} style={{ width: `${percent}%` }} />
        </div>
      </div>
    </Tooltip>
  )
}

export default ResourceQuotaCard
